import React from "react";
import { Camera, Loader } from "tabler-icons-react";
import Divider from "../components/Divider";
import PageHeader from "components/PageHeader";
import useHomeworkHelp, { STEPS, TYPES, getStepTitle } from "../hooks/useHomeworkHelp";
import { useNavigate } from "react-router-dom";

// ============================================
// COMPOSANT PRINCIPAL
// ============================================
function HomeworkHelpPage() {
    const {
        // État
        step,
        selectedType,
        selectedImages,
        selectedFiles,
        isIOS,
        student,

        // Handlers
        handleTypeSelection,
        handleContinueFromInstructions,
        handleImageUpload,
        handleRemoveImage,
        handleSubmit,
        handleReset,
    } = useHomeworkHelp();
    const navigation = useNavigate();

    // ============================================
    // RENDU PAR ÉTAPE
    // ============================================
    const renderSelectType = () => (
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <button
                onClick={() => handleTypeSelection(TYPES.QUIZ)}
                className="flex-1 bg-white border-2 border-primary rounded-xl shadow p-6 flex flex-col items-center hover:bg-primary/10 transition-colors cursor-pointer min-w-[180px]"
            >
                <span className="text-2xl mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg"
                        width="40" height="40" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round"
                        color="rgb(249 115 22 / var(--tw-text-opacity, 1))"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-file-text">
                        <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
                        <path d="M14 2v5a1 1 0 0 0 1 1h5" />
                        <path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" />
                    </svg>
                </span>
                <span className="font-bold text-lg mb-1">Vérifier la leçon</span>
                <span className="text-gray-500 text-sm text-center">Obtenez un quiz à partir de votre exercice</span>
            </button>

            <button
                onClick={() => handleTypeSelection(TYPES.RESOLUTION)}
                className="flex-1 bg-white border-2 border-primary rounded-xl shadow p-6 flex flex-col items-center hover:bg-primary/10 transition-colors cursor-pointer min-w-[180px]"
            >
                <span className="text-2xl mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg"
                        width="40" height="40" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round"
                        color="rgb(34 197 94 / var(--tw-text-opacity, 1))"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-puzzle">
                        <path d="M15.39 4.39a1 1 0 0 0 1.68-.474 2.5 2.5 0 1 1 3.014 3.015 1 1 0 0 0-.474 1.68l1.683 1.682a2.414 2.414 0 0 1 0 3.414L19.61 15.39a1 1 0 0 1-1.68-.474 2.5 2.5 0 1 0-3.014 3.015 1 1 0 0 1 .474 1.68l-1.683 1.682a2.414 2.414 0 0 1-3.414 0L8.61 19.61a1 1 0 0 0-1.68.474 2.5 2.5 0 1 1-3.014-3.015 1 1 0 0 0 .474-1.68l-1.683-1.682a2.414 2.414 0 0 1 0-3.414L4.39 8.61a1 1 0 0 1 1.68.474 2.5 2.5 0 1 0 3.014-3.015 1 1 0 0 1-.474-1.68l1.683-1.682a2.414 2.414 0 0 1 3.414 0z" />
                    </svg>
                </span>
                <span className="font-bold text-lg mb-1">Corriger l'exercice</span>
                <span className="text-gray-500 text-sm text-center">Obtenez la correction de votre exercice</span>
            </button>
        </div>
    );

    const renderInstructions = () => {
        // Instructions adaptées selon la plateforme et le type
        const isQuiz = selectedType === TYPES.QUIZ;
        
        const instructions = isIOS ? (
            isQuiz ? [
                "Ouvrez l'app Photos de votre iPhone.",
                "Prenez les photos des pages dans l'ordre (page 1, puis page 2, etc.).",
                "Revenez sur cette page.",
                "Appuyez sur \"Sélectionner\" puis choisissez \"Photothèque\".",
                "Sélectionnez vos photos dans l'ordre.",
            ] : [
                "Ouvrez l'app Photos de votre iPhone.",
                "Prenez une photo de votre exercice.",
                "Revenez sur cette page.",
                "Appuyez sur \"Sélectionner\" puis choisissez \"Photothèque\".",
                "Sélectionnez votre photo.",
            ]
        ) : (
            isQuiz ? [
                "Ouvrez le cahier à la première page de l'exercice.",
                "Prenez les photos dans l'ordre (page 1, puis page 2, etc.).",
                "Appuyez sur \"Sélectionner des photos\".",
                "Sélectionnez vos photos dans l'ordre de prise.",
            ] : [
                "Ouvrez le cahier à la page de l'exercice.",
                "Prenez une photo de votre exercice.",
                "Appuyez sur \"Sélectionner une photo\".",
                "Sélectionnez votre photo.",
            ]
        );

        return (
            <div className="flex flex-col items-center space-y-3">
                <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: '#D4E7FF' }}>
                    <Camera size={40} style={{ color: '#003B7F' }} />
                </div>

                <h2 className="text-xl font-bold text-center">
                    {isIOS ? "Comment sélectionner vos photos" : "Prenez les pages en photo"}
                </h2>

                {selectedType === TYPES.QUIZ ? (
                    <>
                        <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 w-full max-w-sm">
                            <p className="text-blue-800 text-sm text-center font-semibold">
                                📸 <strong>Important :</strong> Prenez et sélectionnez vos photos dans l'ordre des pages (1, 2, 3...) pour un meilleur résultat.
                            </p>
                        </div>

                        {isIOS && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 w-full max-w-sm">
                                <p className="text-orange-700 text-sm text-center">
                                    ⚠️ Sur iPhone, prenez d'abord toutes les photos depuis l'app Photos, puis sélectionnez-les ici dans l'ordre.
                                </p>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4 w-full max-w-sm">
                        <p className="text-orange-800 text-sm text-center font-semibold">
                            ℹ️ <strong>Note :</strong> La résolution d'exercice n'accepte qu'une seule photo pour le moment.
                        </p>
                    </div>
                )}

                <div className="bg-gray-50 rounded-xl p-6 space-y-4 w-full max-w-sm">
                    {instructions.map((instruction, index) => (
                        <div key={index} className="flex items-start gap-4">
                            <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold text-lg flex-shrink-0">
                                {index + 1}
                            </div>
                            <p className="text-gray-700 ">{instruction}</p>
                        </div>
                    ))}
                </div>

                <button
                    onClick={handleContinueFromInstructions}
                    className="w-full max-w-sm bg-primary text-white px-6 py-4 rounded-full text-lg font-semibold hover:bg-primary/90 transition-colors"
                >
                    Continuer
                </button>

                <button
                    onClick={handleReset}
                    className="text-gray-600 px-6 py-2 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                    Retour
                </button>
            </div>
        );
    };

    const renderUpload = () => (
        <div className="flex flex-col items-center">
            {selectedImages.length > 0 && (
                <div className="w-full mb-4">
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        {selectedImages.map((image, index) => (
                            <div key={index} className="relative">
                                <img
                                    src={image}
                                    alt={`Selected ${index + 1}`}
                                    className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                                />
                                <button
                                    onClick={() => handleRemoveImage(index)}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
                                >
                                    ×
                                </button>
                                <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                                    {index + 1}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <label
                htmlFor="image-upload"
                className={`cursor-pointer bg-primary text-white px-6 py-3 rounded-full flex items-center gap-2 text-lg hover:bg-primary/90 transition-colors ${
                    selectedType === TYPES.RESOLUTION && selectedImages.length >= 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
                <Camera size={24} />
                {selectedType === TYPES.QUIZ 
                    ? (selectedImages.length > 0 ? `Ajouter la page ${selectedImages.length + 1}` : "Sélectionner des photos")
                    : (selectedImages.length > 0 ? "Photo sélectionnée" : "Sélectionner une photo")
                }
            </label>

            {selectedType === TYPES.QUIZ ? (
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 max-w-sm">
                    <p className="text-blue-700 text-xs text-center">
                        💡 <strong>Astuce :</strong> Sélectionnez vos photos dans l'ordre : page 1, puis page 2, etc.
                    </p>
                </div>
            ) : (
                <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg p-3 max-w-sm">
                    <p className="text-orange-700 text-xs text-center">
                        ℹ️ La résolution d'exercice n'accepte qu'une seule photo pour le moment.
                    </p>
                </div>
            )}

            {isIOS && (
                <div className="mt-2 bg-orange-50 border border-orange-200 rounded-lg p-3 max-w-sm">
                    <p className="text-orange-700 text-sm text-center">
                        📱 Choisissez <strong>"Photothèque"</strong> dans le menu qui s'affiche
                    </p>
                </div>
            )}

            {selectedImages.length > 0 && (
                <button
                    onClick={() => {
                        // Passer à l'étape de prévisualisation
                        handleContinueFromInstructions();
                    }}
                    className="mt-4 bg-green-500 text-white px-6 py-3 rounded-full hover:bg-green-600 transition-colors font-semibold"
                >
                    Continuer ({selectedImages.length} photo{selectedImages.length > 1 ? 's' : ''})
                </button>
            )}

            <button
                onClick={handleReset}
                className="mt-4 text-gray-600 px-6 py-2 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors"
            >
                Retour
            </button>
        </div>
    );

    const renderPreview = () => (
        <>
            <div className="mb-6">
                {selectedImages.length === 1 ? (
                    <img
                        src={selectedImages[0]}
                        alt="Selected"
                        className="w-full h-64 object-contain rounded-lg border border-gray-200 shadow-sm"
                    />
                ) : (
                    <div className="space-y-3">
                        <p className="text-center text-gray-600 font-medium mb-3">
                            {selectedImages.length} photos sélectionnées
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            {selectedImages.map((image, index) => (
                                <div key={index} className="relative">
                                    <img
                                        src={image}
                                        alt={`Selected ${index + 1}`}
                                        className="w-full h-40 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
                                    />
                                    <button
                                        onClick={() => handleRemoveImage(index)}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg font-bold"
                                    >
                                        ×
                                    </button>
                                    <div className="absolute bottom-2 left-2 bg-black/70 text-white text-sm px-2 py-1 rounded font-semibold">
                                        Page {index + 1}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <div className="flex flex-col gap-4">
                <button
                    onClick={handleSubmit}
                    className="bg-primary text-white px-6 py-3 rounded-full flex items-center justify-center gap-2 text-lg hover:bg-primary/90 transition-colors"
                >
                    {selectedType === TYPES.QUIZ ? "Générer un quiz" : "Corriger l'exercice"}
                </button>
                <button
                    onClick={handleReset}
                    className="text-gray-600 px-6 py-3 rounded-full flex items-center justify-center gap-2 text-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                    Changer l'option ou les images
                </button>
            </div>
        </>
    );

    const renderProcessing = () => (
        <>
            <div className="mb-6">
                {selectedImages.length === 1 ? (
                    <img
                        src={selectedImages[0]}
                        alt="Selected"
                        className="w-full h-64 object-contain rounded-lg border border-gray-200 shadow-sm opacity-50"
                    />
                ) : (
                    <div className="grid grid-cols-2 gap-3 opacity-50">
                        {selectedImages.map((image, index) => (
                            <div key={index} className="relative">
                                <img
                                    src={image}
                                    alt={`Selected ${index + 1}`}
                                    className="w-full h-40 object-cover rounded-lg border-2 border-gray-200"
                                />
                                <div className="absolute bottom-2 left-2 bg-black/70 text-white text-sm px-2 py-1 rounded">
                                    Page {index + 1}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="flex flex-col gap-4">
                <button
                    disabled
                    className="bg-primary text-white px-6 py-3 rounded-full flex items-center justify-center gap-2 text-lg opacity-70"
                >
                    <Loader className="animate-spin" />
                    {selectedType === TYPES.QUIZ ? "Génération en cours..." : "Correction en cours..."}
                </button>
                <p className="text-center text-gray-500 text-sm">
                    Traitement de {selectedImages.length} photo{selectedImages.length > 1 ? 's' : ''}...
                </p>
            </div>
        </>
    );

    // ============================================
    // MAP DES RENDUS PAR ÉTAPE
    // ============================================
    const stepRenderers = {
        [STEPS.SELECT_TYPE]: renderSelectType,
        [STEPS.INSTRUCTIONS]: renderInstructions,
        [STEPS.UPLOAD]: renderUpload,
        [STEPS.PREVIEW]: renderPreview,
        [STEPS.PROCESSING]: renderProcessing,
    };

    // ============================================
    // RENDU PRINCIPAL
    // ============================================
    return (
        <div className="flex flex-col h-[100dvh]">
            <PageHeader title="Aide aux devoirs" path={-1} />

            <div className="flex-1 overflow-auto">
                <div className="p-2 flex items-center justify-between w-full">
                    <span className="font-bold text-md max-sm:text-sm md:text-md max-w-[60%]">
                        {getStepTitle(step)}
                    </span>
                    <button
                        onClick={() => navigation("/parentProspect/select-enfant")}
                        className="text-sm text-primary underline hover:text-primary/80 min-w-max"
                    >
                        Changer d'enfant
                    </button>
                </div>

                <div className="p-4">
                    <div className="max-w-md mx-auto space-y-1">
                        <input
                            type="file"
                            accept="image/*"
                            multiple={selectedType === TYPES.QUIZ}
                            onChange={handleImageUpload}
                            className="hidden"
                            id="image-upload"
                        />

                        {/* Rendu dynamique selon l'étape */}
                        {stepRenderers[step]?.()}
                    </div>
                </div>

                <Divider />
            </div>
        </div>
    );
}

export default HomeworkHelpPage;
